import React, { useMemo } from 'react';
import { TableRow, TableCell, Box, CircularProgress, Typography } from '@mui/material';
import moment from 'moment';
import { formatCurrency } from 'utils';
import { AuditItem, ChildRowDataProcessorProps } from '../../types';
import { getDataCellClass } from 'components/tables/common/simpleTableClasses';
import {
    ChildRowConditionalColumns,
    getStoreConfigurationFlags,
    transformAuditToAuditItem
} from '../../utils';
import InventoryTicketMetric from './InventoryTicketMetric';
import { AUDIT_TABLE_COLUMN_STYLES } from './auditTableColumnStyles';

const VARIANCE_COLUMN_STYLE: React.CSSProperties = {
    textAlign: 'right',
};
const DATE_COLUMN_STYLE: React.CSSProperties = {
    ...AUDIT_TABLE_COLUMN_STYLES.date,
    paddingLeft: '2px',
    paddingRight: '6px',
};

const formatAuditTime = (item: AuditItem) => {
    const parsed = item?.report_datetime ? moment.parseZone(item.report_datetime) : null;
    return parsed?.isValid() ? parsed.format('h:mm A') : 'Time unavailable';
};

const formatShiftClockTime = (value: unknown) => {
    if (!value) {
        return '';
    }

    const parsed = moment(String(value), ['HH:mm:ss', 'HH:mm'], true);
    return parsed.isValid() ? parsed.format('h:mm A') : String(value);
};

const formatShiftRange = (item: AuditItem) => {
    const startTime = formatShiftClockTime(item?.shift_start_time) || formatAuditTime(item);
    const endTime = formatShiftClockTime(item?.shift_end_time);

    if (startTime && endTime) {
        return `${startTime} to ${endTime}`;
    }

    return startTime || 'Time unavailable';
};

const formatLocationLabel = (item: AuditItem) => {
    const name = String(item?.location_name || '').trim();
    const key = String(item?.location_key || '').trim();
    const type = String(item?.location_type || '').trim();

    if (name) {
        return name;
    }
    if (key) {
        return key;
    }
    if (type) {
        return type;
    }
    return '';
};

const ChildRowDataProcessor: React.FC<ChildRowDataProcessorProps> = ({
    audits,
    isLoadingAudits,
    borderColumns,
    onAuditDetails,
    selectedStore,
    reportDate,
    isBothConnected,
    isBothNotConnected,
    isLottoConnectedOnly,
    isPOSConnectedOnly,
    reconciliationColumnIndex,
    showNetVariance = true,
    hideStockColumns = false,
    backstockColumnOffset = 0
}) => {
    // Extract hasPosType from store flags
    const { hasPosType } = getStoreConfigurationFlags(selectedStore);
    const showBackstockAccounting = backstockColumnOffset > 0;
    const stockStartColumnIndex = showBackstockAccounting ? 8 : 1;
    const backstockColumnIndex = hideStockColumns ? 1 : 4;

    const handleAudit = (event: React.MouseEvent, item: AuditItem) => {
        event.stopPropagation();
        onAuditDetails({ ...item, report_date: reportDate })
    }

    // Transform audits array to AuditItem format and filter
    const auditData = useMemo(() => {
        if (!audits || !Array.isArray(audits)) {
            return [];
        }

        return audits
            .map(transformAuditToAuditItem)
            .sort((a: AuditItem, b: AuditItem) => {
                const aTime = new Date(a?.report_datetime || 0).getTime();
                const bTime = new Date(b?.report_datetime || 0).getTime();
                return aTime - bTime;
            });
    }, [audits]);

    return <>
        {isLoadingAudits ? (
            <TableRow className='child-row-data-processor__no-data-row'>
                <TableCell colSpan={reconciliationColumnIndex + (showNetVariance ? 1 : 0)}>
                    <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 1,
                        }}
                    >
                        <CircularProgress size={16} />
                        <Typography component="span" sx={{ color: '#4B5563', fontWeight: 500 }}>
                            Loading audit details...
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        ) : auditData.length === 0 ?
            <TableRow className='child-row-data-processor__no-data-row'>
                <TableCell colSpan={reconciliationColumnIndex + (showNetVariance ? 1 : 0)}>
                    <p className="text-align-center">No data found</p>
                </TableCell>
            </TableRow>
            : auditData.map((item: AuditItem, rowIdx: number) => {
                const childRowTotalRows = auditData.length;

                return (<TableRow
                    key={rowIdx}
                    className={[
                        'child-row-data-processor__data-row',
                        rowIdx === childRowTotalRows - 1 ? 'child-row-data-processor__data-row--last' : '',
                    ].filter(Boolean).join(' ')}
                >
                    {/* Column 0: Date/Time */}
                    <TableCell
                        className={`child-row-data-processor__cell child-row-data-processor__cell--date-time ${getDataCellClass(0, rowIdx, childRowTotalRows, borderColumns)}`}
                        style={DATE_COLUMN_STYLE}
                    >
                        <div className='audit-table-child-row-head'>
                            <div
                                className="audit-table__child-meta-grid cursor-pointer"
                                onClick={(event) => handleAudit(event, item)}
                            >
                                <div className="audit-table__child-line audit-table__child-line--primary">
                                    <span className="audit-table__child-range">
                                        {formatShiftRange({ ...item, report_date: reportDate } as AuditItem)}
                                    </span>
                                    {item?.is_autogenerated && (
                                        <span className="audit-table__auto-shift-badge">Auto audit</span>
                                    )}
                                </div>
                                <div className="audit-table__child-line audit-table__child-line--secondary">
                                    <div className="audit-table__child-left-block">
                                        <span className="audit-table__child-auditor">
                                            {item?.user_name || item?.operator_name || '-'}
                                        </span>
                                    </div>
                                    {formatLocationLabel(item) ? (
                                        <div className="audit-table__child-right-block">
                                            <span className="audit-table__child-location">{formatLocationLabel(item)}</span>
                                        </div>
                                    ) : (
                                        <span className="audit-table__child-location audit-table__child-location--empty" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </TableCell>

                    {!showBackstockAccounting && !hideStockColumns && (
                        <>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.opening_count}
                                    packs={item?.opening_pack_count}
                                    emphasized={false}
                                />
                            </TableCell>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 1, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.newly_activated_count}
                                    packs={item?.newly_activated_pack_count}
                                    emphasized={false}
                                />
                            </TableCell>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 2, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.closing_count}
                                    packs={item?.closing_pack_count}
                                    emphasized={false}
                                />
                            </TableCell>
                        </>
                    )}

                    {!showBackstockAccounting && (
                        <TableCell
                            className={`child-row-data-processor__cell ${getDataCellClass(backstockColumnIndex, rowIdx, childRowTotalRows, borderColumns)}`}
                            style={VARIANCE_COLUMN_STYLE}
                        >
                            <span className="audit-table__child-backstock-placeholder">-</span>
                        </TableCell>
                    )}

                    {Array.from({ length: showBackstockAccounting ? 7 : 0 }).map((_, index) => (
                        <TableCell
                            key={`backstock-ledger-blank-${index}`}
                            className={`child-row-data-processor__cell ${getDataCellClass(1 + index, rowIdx, childRowTotalRows, borderColumns)}`}
                            style={VARIANCE_COLUMN_STYLE}
                        />
                    ))}

                    {showBackstockAccounting && !hideStockColumns && (
                        <>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.opening_count}
                                    packs={item?.opening_pack_count}
                                    emphasized={false}
                                    zeroPlaceholder="-"
                                />
                            </TableCell>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 1, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.newly_activated_count}
                                    packs={item?.newly_activated_pack_count}
                                    emphasized={false}
                                    zeroPlaceholder="-"
                                />
                            </TableCell>
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 2, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            />
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 3, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            />
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 4, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            />
                            <TableCell
                                className={`child-row-data-processor__cell ${getDataCellClass(stockStartColumnIndex + 5, rowIdx, childRowTotalRows, borderColumns)}`}
                                style={VARIANCE_COLUMN_STYLE}
                            >
                                <InventoryTicketMetric
                                    tickets={item?.closing_count}
                                    packs={item?.closing_pack_count}
                                    emphasized={false}
                                    zeroPlaceholder="-"
                                />
                            </TableCell>
                        </>
                    )}

                    {/* Column 5: Sales */}
                    {!hasPosType && (
                        <TableCell
                            className={`child-row-data-processor__cell ${getDataCellClass(5 + backstockColumnOffset, rowIdx, childRowTotalRows, borderColumns)}`}
                            style={VARIANCE_COLUMN_STYLE}
                        >
                            {item?.total_sold ?? 0}
                        </TableCell>
                    )}

                    {/* Column 6: Sales $ */}
                    {!hasPosType && (
                        <TableCell
                            className={`child-row-data-processor__cell ${getDataCellClass(6 + backstockColumnOffset, rowIdx, childRowTotalRows, borderColumns)}`}
                            style={VARIANCE_COLUMN_STYLE}
                        >
                            {formatCurrency(item?.revenue ?? 0)}
                        </TableCell>
                    )}

                    {/* Conditional Columns */}
                    <ChildRowConditionalColumns
                        item={item}
                        isBothConnected={isBothConnected}
                        isBothNotConnected={isBothNotConnected}
                        isLottoConnectedOnly={isLottoConnectedOnly}
                        isPOSConnectedOnly={isPOSConnectedOnly}
                        borderColumns={borderColumns}
                        hasPosType={hasPosType}
                        rowIdx={rowIdx}
                        totalRows={childRowTotalRows}
                        columnOffset={backstockColumnOffset}
                        hideLottoAndPayout={showBackstockAccounting}
                    />

                    {showNetVariance && (
                        <TableCell
                            className={`child-row-data-processor__cell ${getDataCellClass(reconciliationColumnIndex, rowIdx, childRowTotalRows, borderColumns)}`}
                            style={VARIANCE_COLUMN_STYLE}
                        />
                    )}
                </TableRow>)
            })}
    </>
};

export default ChildRowDataProcessor; 
